/**
 * Beneficiary management view (task 21.2): list, create, update, delete
 * (R3.1–R3.4).
 *
 * A form doubles as create (when no beneficiary is selected for editing) and
 * update (when one is). Field-level validation errors returned by the service
 * (`INVALID_BENEFICIARY_FIELD` with a `field`, R3.9) render inline next to the
 * offending input; other envelope errors render at the form level. On success
 * the list reloads. Delete confirms before calling the service.
 *
 * Client-side input guidance (defence in depth — the server stays
 * authoritative): the account-number field is masked so only 6–11 digits can
 * be typed, the bank code is chosen from a fixed dropdown of real SA banks so
 * it is always a valid 6-digit universal branch code, each constrained field
 * carries helper text linked via `aria-describedby`, a gentle live hint nudges
 * the user while the account number is not yet a valid length, and the submit
 * button is disabled until the form is client-valid.
 *
 * Accessible markup: labelled inputs tied via `htmlFor`/`id`, `aria-invalid`
 * on flagged fields, an assertive error region, and a semantic list of rows.
 */

import { useId, useState, type FormEvent } from 'react';

import { beneficiariesApi, type Beneficiary, type BeneficiaryInput } from '../api/index.js';
import { ErrorNotice, fieldErrorMessage } from '../ui/ErrorNotice.js';
import { SA_BANKS, bankOptionLabel, isKnownBankCode } from '../ui/sa-banks.js';
import { useAction, useAsyncData } from '../ui/useAsync.js';

const EMPTY_FORM: BeneficiaryInput = {
  displayName: '',
  accountNumber: '',
  bankCode: '',
};

/** Server-mirrored account-number bounds (see @esb/beneficiary). */
const MIN_ACCOUNT_DIGITS = 6;
const MAX_ACCOUNT_DIGITS = 11;
/** Server-mirrored bank-code length (6-digit SA universal branch code). */
const BANK_CODE_LENGTH = 6;

/** The create/update form for a beneficiary. */
function BeneficiaryForm({
  editing,
  onSaved,
  onCancelEdit,
}: {
  readonly editing: Beneficiary | undefined;
  readonly onSaved: () => void;
  readonly onCancelEdit: () => void;
}): JSX.Element {
  const [form, setForm] = useState<BeneficiaryInput>(EMPTY_FORM);
  const nameId = useId();
  const accountId = useId();
  const accountHintId = useId();
  const bankId = useId();
  const bankHintId = useId();

  // Seed the form when entering/leaving edit mode.
  const [seededFor, setSeededFor] = useState<string | undefined>(undefined);
  const editingId = editing?.beneficiaryId;
  if (editingId !== seededFor) {
    setSeededFor(editingId);
    setForm(
      editing !== undefined
        ? {
            displayName: editing.displayName,
            accountNumber: editing.accountNumber,
            bankCode: editing.bankCode,
          }
        : EMPTY_FORM,
    );
  }

  const save = useAction(async (input: BeneficiaryInput) => {
    if (editing !== undefined) {
      return beneficiariesApi.updateBeneficiary(editing.beneficiaryId, input);
    }
    return beneficiariesApi.createBeneficiary(input);
  });

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const result = await save.run({
      displayName: form.displayName.trim(),
      accountNumber: form.accountNumber.trim(),
      bankCode: form.bankCode.trim(),
    });
    if (result !== undefined) {
      setForm(EMPTY_FORM);
      onSaved();
      if (editing !== undefined) {
        onCancelEdit();
      }
    }
  }

  const accountDigits = form.accountNumber;
  const accountLengthValid =
    accountDigits.length >= MIN_ACCOUNT_DIGITS &&
    accountDigits.length <= MAX_ACCOUNT_DIGITS;
  const bankCodeValid = /^\d{6}$/.test(form.bankCode);

  const canSubmit =
    form.displayName.trim().length > 0 &&
    accountLengthValid &&
    bankCodeValid &&
    !save.pending;

  const nameError = fieldErrorMessage(save.error, 'displayName');
  const accountError = fieldErrorMessage(save.error, 'accountNumber');
  const bankError = fieldErrorMessage(save.error, 'bankCode');
  // Show the form-level notice only when the error is not tied to a field.
  const formLevelError =
    nameError === undefined && accountError === undefined && bankError === undefined
      ? save.error
      : undefined;

  // Gentle live nudge while the account number is started but not yet a valid
  // length (1..5 digits). Reserve the harsh red server error for real
  // rejections; this is an informational, not-yet-valid hint.
  const showAccountNudge =
    accountDigits.length > 0 && accountDigits.length < MIN_ACCOUNT_DIGITS;

  // Options for the bank picker. If we are editing a beneficiary whose stored
  // code is not one of the known banks, add it as an extra option so the edit
  // round-trips instead of silently dropping the value.
  const extraBankOption =
    form.bankCode.length > 0 && !isKnownBankCode(form.bankCode)
      ? form.bankCode
      : undefined;

  return (
    <form onSubmit={(event) => void onSubmit(event)} aria-labelledby="beneficiary-form-heading" noValidate className="esb-card">
      <h3 id="beneficiary-form-heading" className="esb-subhead">
        {editing !== undefined ? 'Edit beneficiary' : 'Add beneficiary'}
      </h3>

      <ErrorNotice error={formLevelError} />

      <div className="esb-field">
        <label htmlFor={nameId} className="esb-field__label">Name</label>
        <input
          id={nameId}
          className="esb-field__input"
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          aria-invalid={nameError !== undefined}
          maxLength={100}
          disabled={save.pending}
          required
        />
        {nameError !== undefined ? <span role="alert" className="esb-inline-error">{nameError}</span> : null}
      </div>

      <div className="esb-field">
        <label htmlFor={accountId} className="esb-field__label">Account number</label>
        <input
          id={accountId}
          className="esb-field__input"
          value={form.accountNumber}
          onChange={(e) =>
            setForm({
              ...form,
              // Mask: keep digits only and cap at the server maximum so an
              // invalid character or over-length value never appears.
              accountNumber: e.target.value.replace(/\D/g, '').slice(0, MAX_ACCOUNT_DIGITS),
            })
          }
          inputMode="numeric"
          autoComplete="off"
          maxLength={MAX_ACCOUNT_DIGITS}
          pattern="\d*"
          aria-describedby={accountHintId}
          aria-invalid={accountError !== undefined}
          disabled={save.pending}
          required
        />
        <span id={accountHintId} className="esb-field__hint">
          {MIN_ACCOUNT_DIGITS} to {MAX_ACCOUNT_DIGITS} digits, numbers only.
        </span>
        {showAccountNudge ? (
          <span className="esb-field__hint esb-field__hint--warn">
            Enter at least {MIN_ACCOUNT_DIGITS} digits.
          </span>
        ) : null}
        {accountError !== undefined ? <span role="alert" className="esb-inline-error">{accountError}</span> : null}
      </div>

      <div className="esb-field">
        <label htmlFor={bankId} className="esb-field__label">Bank</label>
        <select
          id={bankId}
          className="esb-select"
          value={form.bankCode}
          onChange={(e) => setForm({ ...form, bankCode: e.target.value })}
          aria-describedby={bankHintId}
          aria-invalid={bankError !== undefined}
          disabled={save.pending}
          required
        >
          <option value="" disabled>
            Select a bank
          </option>
          {SA_BANKS.map((bank) => (
            <option key={bank.code} value={bank.code}>
              {bankOptionLabel(bank)}
            </option>
          ))}
          {extraBankOption !== undefined ? (
            <option value={extraBankOption}>
              Existing code ({extraBankOption})
            </option>
          ) : null}
        </select>
        <span id={bankHintId} className="esb-field__hint">
          Choose the recipient&apos;s bank ({BANK_CODE_LENGTH}-digit branch code).
        </span>
        {bankError !== undefined ? <span role="alert" className="esb-inline-error">{bankError}</span> : null}
      </div>

      <button type="submit" className="esb-btn esb-btn--primary" disabled={!canSubmit}>
        {save.pending ? 'Saving…' : editing !== undefined ? 'Save changes' : 'Add'}
      </button>
      {editing !== undefined ? (
        <button type="button" className="esb-btn esb-btn--ghost" onClick={onCancelEdit} disabled={save.pending}>
          Cancel
        </button>
      ) : null}
    </form>
  );
}

/** The beneficiaries management panel: list + create/edit form + delete. */
export function BeneficiariesView(): JSX.Element {
  const beneficiaries = useAsyncData(() => beneficiariesApi.listBeneficiaries(), []);
  const [editing, setEditing] = useState<Beneficiary | undefined>(undefined);

  const remove = useAction(async (id: string) => {
    await beneficiariesApi.deleteBeneficiary(id);
  });

  async function onDelete(beneficiary: Beneficiary): Promise<void> {
    const confirmed = globalThis.confirm(
      `Delete beneficiary "${beneficiary.displayName}"?`,
    );
    if (!confirmed) {
      return;
    }
    const done = await remove.run(beneficiary.beneficiaryId);
    if (done !== undefined) {
      if (editing?.beneficiaryId === beneficiary.beneficiaryId) {
        setEditing(undefined);
      }
      beneficiaries.reload();
    }
  }

  const list = beneficiaries.data?.beneficiaries ?? [];

  return (
    <section aria-labelledby="beneficiaries-heading">
      <h2 id="beneficiaries-heading" className="esb-section-title">Beneficiaries</h2>

      <ErrorNotice error={beneficiaries.error} />
      <ErrorNotice error={remove.error} />

      <div className="esb-split">
        <div className="esb-card">
          <div aria-live="polite">
            {beneficiaries.loading ? <p className="esb-loading">Loading beneficiaries…</p> : null}
          </div>

          {!beneficiaries.loading && list.length === 0 && beneficiaries.error === undefined ? (
            <p className="esb-empty">No beneficiaries yet.</p>
          ) : (
            <ul className="esb-bene-list">
              {list.map((beneficiary) => (
                <li key={beneficiary.beneficiaryId} className="esb-bene">
                  <span className="esb-bene__avatar" aria-hidden="true">
                    {initials(beneficiary.displayName)}
                  </span>
                  <span className="esb-bene__meta">
                    <span className="esb-bene__name">{beneficiary.displayName}</span>
                    <span className="esb-bene__sub">
                      {beneficiary.accountNumber} · {beneficiary.bankCode}
                    </span>
                  </span>
                  <span className="esb-bene__actions">
                    <button
                      type="button"
                      className="esb-btn esb-btn--ghost esb-btn--sm"
                      onClick={() => setEditing(beneficiary)}
                      disabled={remove.pending}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="esb-btn esb-btn--danger esb-btn--sm"
                      onClick={() => void onDelete(beneficiary)}
                      disabled={remove.pending}
                    >
                      Delete
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <BeneficiaryForm
          editing={editing}
          onSaved={beneficiaries.reload}
          onCancelEdit={() => setEditing(undefined)}
        />
      </div>
    </section>
  );
}

/** Up to two uppercase initials from a display name, for the avatar circle. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase();
}
